import { FC } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import {
  Actions,
  CloseButton,
  StyledDialog,
} from "@/components/AlertDialogs/Dialog";
import { Modal } from "@/components/Modal";
import Text from "@/components/Text";
import Button from "@/components/Button";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { useQueryClient } from "@tanstack/react-query";
import { OptimisationTestsGroupResource } from "@/types/resources";
import { useForm } from "react-hook-form";
import { handleFormErrors } from "@/utils/helpers";
import FormField from "@/components/Forms/FormField";
import { FormLayout } from "@/components/Forms";

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

const StyledFormLayout = styled(FormLayout)`
  text-align: left;
`;

const sortOptions = [
  {
    value: "quartile_p25_rate",
    label: "25% Reach",
  },
  {
    value: "quartile_p50_rate",
    label: "50% Reach",
  },
  {
    value: "quartile_p75_rate",
    label: "75% Reach",
  },
  {
    value: "quartile_p100_rate",
    label: "100% Reach",
  },
  {
    value: "scene_duration",
    label: "Scene duration",
  },
];

interface FormDataT {
  name: string;
  sort: string;
}

interface Props {
  onClose: () => void;
  onGroupCreated?: (group: OptimisationTestsGroupResource) => void;
  editableGroup?: OptimisationTestsGroupResource;
}

const GroupCreationModal: FC<Props> = ({
  onClose,
  onGroupCreated,
  editableGroup,
}) => {
  const isEditMode = !!editableGroup;

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: "",
      sort: "quartile_p25_rate",
    },
  });

  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const { id: projectId } = useParams();

  const onSave = handleSubmit(async (data) => {
    try {
      const newGroup = await (isEditMode
        ? api.put<{ name: string }, OptimisationTestsGroupResource>(
            `/v1/optimisation-tests-groups/${editableGroup.id}`,
            {
              name: data.name,
            }
          )
        : api.post<
            typeof data & { optimisation_test_id: number },
            OptimisationTestsGroupResource
          >(`/v1/optimisation-tests-groups`, {
            optimisation_test_id: parseInt(projectId!),
            ...data,
          }));

      onGroupCreated?.(newGroup);

      queryClient.setQueryData<OptimisationTestsGroupResource[]>(
        ["tips-tests-groups", { projectId }],
        (groups) => {
          if (isEditMode) {
            return groups?.map((g) => (g.id === newGroup.id ? newGroup : g));
          }
          return [...(groups ?? []), newGroup];
        }
      );

      alerts.success(
        isEditMode
          ? "Group successfully updated"
          : "New group successfully created",
        data.name
      );

      onClose();
    } catch (err) {
      handleFormErrors(err, setError, alerts);
    }
  });

  return (
    <Modal zIndex={50}>
      <StyledDialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Heading $type="heading2">{isEditMode ? "Edit" : "Add"} group</Heading>
        <StyledFormLayout>
          <FormField<FormDataT>
            control={control}
            name="name"
            label="Group name"
          />
          {!isEditMode && (
            <FormField<FormDataT, string>
              control={control}
              name="sort"
              label="Default sort"
              type="select"
              options={sortOptions}
            />
          )}
        </StyledFormLayout>
        <Actions>
          <Button
            onClick={onSave}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </Button>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
        </Actions>
      </StyledDialog>
    </Modal>
  );
};

export default GroupCreationModal;
